<template>
  <div class="shell" :class="{ 'shell--nav-open': navOpen, 'shell--rail': railed }">
    <!-- ── Sidebar ─────────────────────────────────────────────────────── -->
    <aside class="sidebar" :class="{ 'sidebar--rail': railed }">
      <div class="sidebar__brand">
        <!-- The logo comes from the brand and not from the company profile: the
             brand is remembered between visits, so a reload paints it from the
             first frame instead of the initials it used to swap out. -->
        <BrandMark kind="logo" :size="32" plate />
        <span v-if="!railed" class="sidebar__name truncate">
          <UiSkeleton v-if="brandPending" width="96px" height="12px" />
          <template v-else>{{ brandName }}</template>
        </span>
      </div>

      <nav class="sidebar__nav" :aria-label="t('nav.menu')">
        <!--
          ── Collapsed ───────────────────────────────────────────────────────
          One icon per destination, from every section, folded or not: see
          `railItems` in app/navState.js for why the folds are ignored here.
          The label, and whatever hangs under the entry, live in the flyout —
          which opens on hover and on keyboard focus alike.
        -->
        <template v-if="railed">
          <template v-for="row in rail" :key="row.item.to">
            <hr v-if="row.startsGroup" class="sidebar__divider" />

            <RouterLink
              :to="row.item.to"
              :target="row.item.newTab ? '_blank' : undefined"
              :rel="row.item.newTab ? 'noopener' : undefined"
              class="sidebar__link sidebar__link--rail"
              :class="{ 'sidebar__link--active': isActive(row.item) || anyChildActive(row.item, isActive) }"
              @mouseenter="openFlyout($event, row.item)"
              @focus="openFlyout($event, row.item)"
              @blur="scheduleClose"
              @click="closeFlyout"
            >
              <AppIcon :name="row.item.icon" :size="20" />
              <span class="sr-only">{{ t(row.item.labelKey) }}</span>
            </RouterLink>
          </template>
        </template>

        <!-- ── Expanded ─────────────────────────────────────────────────── -->
        <div v-for="group in groups" v-else :key="group.id" class="sidebar__group">
          <!--
            A section folds away when it is not what you came for. The platform
            ships nine entries of its own; a client with ten modules should not
            have to scroll past them to reach the work they paid for.

            A group may also carry no `labelKey` at all: the first one is where
            you land, and a heading over the top three entries labels nothing
            the reader could confuse them with.
          -->
          <button
            v-if="group.collapsible !== false"
            type="button"
            class="sidebar__group-title sidebar__group-toggle"
            :class="{ 'sidebar__group-toggle--current': holdsCurrent(group) }"
            :aria-expanded="isOpen(group)"
            :aria-controls="`nav-${group.id}`"
            @click="toggleGroup(group)"
          >
            <AppIcon name="chevron" :size="14" class="sidebar__chevron"
                     :class="{ 'sidebar__chevron--closed': !isOpen(group) }" />
            <span class="grow">{{ t(group.labelKey) }}</span>
            <!--
              What is hidden in there, so a closed section is not a mystery —
              and, when the page being read is one of them, in the accent: a
              section you closed while standing in it still says so.
            -->
            <span v-if="!isOpen(group)" class="sidebar__count">{{ group.items.length }}</span>
          </button>

          <p v-else-if="group.labelKey" class="sidebar__group-title">{{ t(group.labelKey) }}</p>

          <div :id="`nav-${group.id}`" class="sidebar__items" :class="{ 'sidebar__items--closed': !isOpen(group) }">
            <div class="sidebar__items-inner">
              <template v-for="item in group.items" :key="item.to">
                <!--
                  De qué módulo es lo que viene. Solo cuando el módulo aporta
                  más de una entrada: si aporta una, el nombre del módulo y el
                  de la pantalla dicen casi lo mismo y el rótulo sería una línea
                  de más por cada cosa instalada.

                  Con once módulos delante, «Mi jornada» y «Fichajes» se leían
                  como dos productos distintos. Son las dos caras de uno: la de
                  la persona y la de la oficina.
                -->
                <p v-if="item.band?.first" class="sidebar__band-title" :style="bandStyle(item.band)">
                  {{ moduleLabel(item.band.module) }}
                </p>

                <!--
                  An entry with pages under it. A module with six screens
                  cannot take six of the few slots up here, and cannot hide
                  five of them behind the sixth either — so it declares them
                  as children and this draws the fold.

                  The row is a link AND a toggle, not one or the other: the
                  label goes to the section's own page, the chevron opens what
                  is inside it. Making the whole row a toggle would mean the
                  entry named after the section is the one thing you cannot
                  click to reach it.
                -->
                <div
                  v-if="item.children?.length"
                  class="sidebar__branch"
                  :class="bandClass(item)"
                  :style="bandStyle(item.band)"
                >
                  <!--
                    The row is marked when it is the page you are on, and also
                    when it is closed over the page you are on: folding is the
                    reader's decision, so it must not be able to lose them.
                  -->
                  <div
                    class="sidebar__row"
                    :class="{
                      'sidebar__link--active': isActive(item),
                      'sidebar__row--current': branchHoldsCurrent(item),
                    }"
                  >
                    <RouterLink
                      :to="item.to"
                      class="sidebar__link sidebar__link--parent"
                      :tabindex="isOpen(group) ? undefined : -1"
                      @click="navOpen = false"
                    >
                      <AppIcon :name="item.icon" />
                      <span class="truncate">{{ t(item.labelKey) }}</span>
                    </RouterLink>

                    <button
                      type="button"
                      class="sidebar__disclose"
                      :aria-expanded="isBranchOpen(item)"
                      :aria-controls="`nav-branch-${slug(item.to)}`"
                      :aria-label="t('nav.section', { name: t(item.labelKey) })"
                      :tabindex="isOpen(group) ? undefined : -1"
                      @click="toggleBranch(item)"
                    >
                      <AppIcon name="chevron" :size="14" class="sidebar__chevron"
                               :class="{ 'sidebar__chevron--closed': !isBranchOpen(item) }" />
                    </button>
                  </div>

                  <div
                    :id="`nav-branch-${slug(item.to)}`"
                    class="sidebar__items sidebar__subitems"
                    :class="{ 'sidebar__items--closed': !isBranchOpen(item) }"
                  >
                    <div class="sidebar__items-inner">
                      <RouterLink
                        v-for="child in item.children"
                        :key="child.to"
                        :to="child.to"
                        class="sidebar__link sidebar__link--child"
                        :class="{ 'sidebar__link--active': isActive(child) }"
                        :tabindex="isOpen(group) && isBranchOpen(item) ? undefined : -1"
                        @click="navOpen = false"
                      >
                        <span class="truncate">{{ t(child.labelKey) }}</span>
                      </RouterLink>
                    </div>
                  </div>
                </div>

                <RouterLink
                  v-else
                  :to="item.to"
                  :target="item.newTab ? '_blank' : undefined"
                  :rel="item.newTab ? 'noopener' : undefined"
                  class="sidebar__link"
                  :class="[{ 'sidebar__link--active': isActive(item) }, bandClass(item)]"
                  :style="bandStyle(item.band)"
                  :tabindex="isOpen(group) ? undefined : -1"
                  @click="navOpen = false"
                >
                  <AppIcon :name="item.icon" />
                  <span class="truncate">{{ t(item.labelKey) }}</span>
                  <!-- Lo que espera ahí dentro (los correos sin leer): lo trae la
                       entrada como un valor vivo, y el menú solo lo pinta. -->
                  <span v-if="badgeOf(item)" class="sidebar__badge">{{ badgeOf(item) }}</span>
                </RouterLink>
              </template>
            </div>
          </div>
        </div>
      </nav>

      <!--
        The version, and the control that hands the page back 184px of window.
        At the bottom rather than up by the brand: collapsing is something you
        do once a week, and a button in the top-left corner of the application
        reads as the primary action of the whole sidebar.
      -->
      <footer class="sidebar__foot">
        <span v-if="!railed" class="text-xs truncate">{{ t('nav.platform') }} {{ platformVersion }}</span>

        <button
          type="button"
          class="sidebar__collapse"
          :aria-label="railed ? t('nav.expand') : t('nav.collapse')"
          :title="`${railed ? t('nav.expand') : t('nav.collapse')} (Ctrl+B)`"
          @click="toggleRail"
        >
          <AppIcon name="chevron" :size="16" class="sidebar__collapse-icon" />
        </button>
      </footer>
    </aside>

    <!--
      What a railed entry is called, and what hangs under it. Teleported to the
      body because the nav scrolls, and a panel drawn inside a scrolling box is
      a panel clipped at its own edge.
    -->
    <Teleport to="body">
      <div
        v-if="flyout"
        ref="flyoutEl"
        class="flyout"
        :style="{ top: `${flyout.top}px` }"
        @mouseenter="holdFlyout"
        @mouseleave="scheduleClose"
      >
        <RouterLink
          v-if="flyout.item.children?.length"
          :to="flyout.item.to"
          class="flyout__title flyout__title--link"
          @click="closeFlyout"
        >{{ t(flyout.item.labelKey) }}</RouterLink>
        <p v-else class="flyout__title">{{ t(flyout.item.labelKey) }}</p>

        <RouterLink
          v-for="child in flyout.item.children ?? []"
          :key="child.to"
          :to="child.to"
          class="flyout__link"
          :class="{ 'flyout__link--active': isActive(child) }"
          @click="closeFlyout"
        >{{ t(child.labelKey) }}</RouterLink>
      </div>
    </Teleport>

    <!-- Backdrop for the mobile drawer -->
    <div v-if="navOpen" class="shell__backdrop" @click="navOpen = false" />

    <!-- ── Main ────────────────────────────────────────────────────────── -->
    <div class="shell__main">
      <!--
        Above the top bar and above everything else, because on a demo instance
        it is the first thing that has to be true: none of this is your data.
        It renders nothing at all on a real client's ERP.
      -->
      <DemoBanner />

      <header class="topbar">
        <button type="button" class="topbar__burger" :aria-label="t('nav.menu')" @click="navOpen = !navOpen">
          <AppIcon name="menu" :size="20" />
        </button>

        <GlobalSearch />

        <div class="grow" />

        <!-- The assistant is not here: it lives as a tab on the right edge of
             the window, so the bar stays about the page you are on. This still
             mounts it, and with it the Ctrl+J shortcut. -->
        <AssistantButton />

        <!--
          On a phone the bar keeps only what you reach for while working:
          the menu, search, notifications and yourself. Installing the app and
          switching the theme are things you do once, so they move inside the
          user menu below rather than each taking 36px of a 390px-wide bar.
        -->
        <InstallButton compact class="topbar__wide" />

        <button
          type="button"
          class="topbar__icon topbar__wide"
          :aria-label="themeTitle"
          :title="themeTitle"
          @click="cyclePreference"
        >
          <AppIcon :name="THEME_ICONS[themePreference]" />
        </button>

        <!-- En el teléfono el asistente va aquí, junto a la campana: el botón
             flotante tapaba controles en las fichas y en el parte, y arriba es
             donde se busca lo que no es la página. En escritorio sigue siendo
             la pestaña del borde (AssistantButton). -->
        <button
          type="button"
          class="topbar__icon topbar__phone"
          :aria-label="t('assistant.open')"
          :title="t('assistant.title')"
          @click="assistant.toggle()"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M12 3l1.9 4.6L18.5 9.5l-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9z" />
            <path d="M18 15l.9 2.1L21 18l-2.1.9L18 21l-.9-2.1L15 18l2.1-.9z" />
          </svg>
        </button>

        <NotificationBell />

        <div class="usermenu">
          <button
            type="button"
            class="usermenu__trigger"
            :aria-expanded="userOpen"
            @click.stop="userOpen = !userOpen"
          >
            <span class="usermenu__avatar" aria-hidden="true">{{ initials(auth.user?.name) }}</span>
            <span class="usermenu__name truncate">{{ auth.user?.name }}</span>
          </button>

          <Transition name="slide-up">
            <div v-if="userOpen" class="usermenu__panel">
              <p class="usermenu__email truncate">{{ auth.user?.email }}</p>
              <RouterLink class="usermenu__item" to="/profile" @click="userOpen = false">
                <AppIcon name="user" :size="16" /> {{ t('profile.title') }}
              </RouterLink>

              <!-- The other half of the top bar, on the screens where the bar
                   has no room for it. Hidden on a wide window, where it is one
                   click away up there. -->
              <div class="usermenu__narrow">
                <InstallButton class="usermenu__install" />
              </div>

              <!-- The three themes side by side, on every width. The top-bar
                   button only steps through them; this is where «Auto» can be
                   seen and chosen without guessing what the icon means. -->
              <div class="usermenu__section">
                <p class="usermenu__label">{{ t('nav.themeLabel') }}</p>
                <ThemeSwitcher />
              </div>

              <!-- The language belongs to the person, not to the page, so it
                   sits with the rest of what is theirs instead of taking four
                   permanent slots in the top bar. -->
              <div class="usermenu__section">
                <p class="usermenu__label">{{ t('nav.language') }}</p>
                <LanguageSwitcher />
              </div>

              <button type="button" class="usermenu__item" @click="signOut">
                <AppIcon name="logout" :size="16" /> {{ t('nav.signOut') }}
              </button>
            </div>
          </Transition>
        </div>
      </header>

      <!-- Tirar hacia abajo para recargar (app/pullToRefresh.js): el círculo
           sigue al dedo, gira al pasar el umbral y da vueltas mientras recarga. -->
      <div
        v-if="pullDistance > 0 || pullRefreshing"
        class="pull"
        :class="{ 'pull--armed': pullArmed, 'pull--busy': pullRefreshing }"
        :style="{ transform: `translate(-50%, ${pullDistance}px)` }"
        role="status"
        :aria-label="t('common.refresh')"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"
             :style="{ transform: `rotate(${pullRefreshing ? 0 : pullDistance * 3}deg)` }">
          <path d="M21 12a9 9 0 1 1-2.64-6.36" /><path d="M21 3v6h-6" />
        </svg>
      </div>

      <!-- La raya de «yendo»: solo si la pantalla nueva tarda en llegar
           (app/pageTransition.js, useNavigationProgress). -->
      <div v-if="navigating" class="nav-progress" role="progressbar" :aria-label="t('common.loading')" />

      <main ref="pane" class="shell__content">
        <!-- ── Una ficha por cada ficha ────────────────────────────────────
             `key` por el CAMINO, y no es cosmético.

             Vue Router reutiliza el componente cuando solo cambia un parámetro,
             y ninguna de las 49 pantallas de ficha del ERP observa su `:id`:
             todas cargan en `onMounted`. Así que ir de un cliente a otro —lo que
             hace el buscador global, que está en todas las pantallas con Ctrl+K—
             dejaba la URL diciendo una cosa y la pantalla enseñando otra.
             Medido: se abre la ficha A, se salta a la B, y el título sigue
             siendo el de A.

             Por `fullPath` y no por el camino, y eso arregla la otra mitad del
             mismo problema: NINGUNA de las 25 pantallas que leen `route.query`
             la observa —todas la leen en `onMounted`— así que ir de
             `/incidents` a `/incidents?subjectType=asset&subjectId=…`, que es lo
             que hace el panel de la ficha de una máquina, dejaba la lista
             anterior en pantalla con la URL diciendo otra cosa.

             Y lo que obligó a poner `path` en su día —que una lista que guarda
             sus filtros en la URL se remonte en cada clic— se resuelve al
             revés: esas pantallas lo DICEN en su ruta con `meta.ownsQuery`, y
             solo ellas se quedan con el camino. Son ocho, y son ocho porque se
             buscaron una a una (`router.replace({ query })` sobre su propio
             camino), no porque se supusiera.

             ── La transición ──
             Con el dedo, la pantalla nueva entra deslizándose (desde la
             derecha al avanzar, desde la izquierda al volver); con ratón o con
             «menos movimiento», el fundido de siempre (app/pageTransition.js).
             `out-in` sigue: la vieja se va antes de que entre la nueva, así
             que nunca hay dos pantallas a la vez en el panel ni salta el
             armazón. Y como la transición solo corre cuando la `key` cambia,
             su `@enter` es el momento justo de devolver el scroll al volver
             atrás (app/scrollMemory.js). Los guardas de cambios sin guardar
             deciden antes, en la navegación: si alguien se queda, no hay
             transición que correr. -->
        <RouterView v-slot="{ Component }">
          <Transition :name="pageTransition" mode="out-in" @enter="scrollMemory.onViewEntered">
            <component :is="Component" :key="viewKey" />
          </Transition>
        </RouterView>
      </main>

      <!-- La ventana de escribir un correo, sobre cualquier pantalla: «Enviar
           por email» en una factura la abre sin salir de la factura. -->
      <MailComposeHost />

      <!-- Cómo se instala en Safari (iPhone, iPad, Mac), que no tiene diálogo
           propio. Aquí y no dentro del botón: el de la ventana estrecha vive en
           el menú de usuario, y el clic que abre la guía cierra ese menú. -->
      <InstallGuide
        v-if="installGuideOpen && manualInstall"
        :platform="manualInstall"
        @close="installGuideOpen = false"
      />

    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted, unref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { initials, useTheme, onThemeChosen, UiSkeleton, confirmDiscardingUnsaved } from '@general/ui-kit'
import { usePullToRefresh } from '../app/pullToRefresh'
import { createScrollMemory } from '../app/scrollMemory'
import { usePageTransition, useNavigationProgress } from '../app/pageTransition'
import { useAuthStore } from '@general/auth/frontend'
import { brandName, brandPending, BrandMark } from '@general/companies/frontend'
import { NotificationBell } from '@general/notifications/frontend'
import { AssistantButton, useAssistantStore } from '@general/assistant/frontend'
import { DemoBanner, isDemoInstance } from '@general/demo/frontend'
import { MailComposeHost } from '@general/mail/frontend'

import AppIcon from '../components/AppIcon.vue'
import LanguageSwitcher from '../components/LanguageSwitcher.vue'
import ThemeSwitcher from '../components/ThemeSwitcher.vue'
import { THEME_ICONS } from '../app/themeIcons'
import { preferenceSaver } from '../app/accountPreferences'
import GlobalSearch from '../components/GlobalSearch.vue'
import InstallButton from '../components/InstallButton.vue'
import InstallGuide from '../components/InstallGuide.vue'
import { installGuideOpen, manualInstall } from '../app/pwa'
import { visibleNavigation } from '../app/navigation'
import {
  activeEntry,
  anyChildActive,
  branchKey,
  holdsActive,
  isBranchOpen as branchIsOpen,
  isGroupOpen,
  railItems,
  readFolds,
  readRail,
  toggleFold,
  writeFolds,
  writeRail,
} from '../app/navState'

const { t, te } = useI18n()
/** A live count an entry brings with it (`badge`: a ref or a number). */
const badgeOf = (item) => {
  const value = Number(unref(item.badge)) || 0
  return value > 99 ? '99+' : value || ''
}

const route = useRoute()

/**
 * Qué hace que la pantalla se vuelva a montar.
 *
 * `fullPath` para casi todas: cambiar la consulta con la misma ruta es entrar a
 * mirar otra cosa, y ninguna pantalla del ERP observa su consulta.
 *
 * `path` para las que la escriben ellas (`meta.ownsQuery`): ahí la consulta es
 * el estado de la pantalla —la carpeta abierta, la pestaña, los filtros— y
 * remontar en cada clic sería recargarla entera cada vez que alguien toca algo.
 */
const viewKey = computed(() => (route.meta?.ownsQuery ? route.path : route.fullPath))
const router = useRouter()
const auth = useAuthStore()
const assistant = useAssistantStore()

const navOpen = ref(false)

// ── Tirar hacia abajo para recargar (solo con el dedo) ──────────────────────
// Recarga la página entera, como espera quien lo hace; con un formulario a
// medias pregunta antes, con las palabras de ese formulario.
const pane = ref(null)
const { pull: pullDistance, armed: pullArmed, refreshing: pullRefreshing } = usePullToRefresh(pane, {
  canStart: () => !navOpen.value,
  onRefresh: async () => {
    if (!(await confirmDiscardingUnsaved())) return
    window.location.reload()
    // La recarga tarda un instante: el círculo sigue girando hasta entonces.
    await new Promise(() => {})
  },
})
// ── Pasar de pantalla como una app ──────────────────────────────────────────
// La transición del RouterView (deslizar con el dedo, fundir con ratón), el
// scroll del panel guardado por entrada del historial y devuelto al volver, y
// la raya de arriba cuando la pantalla nueva tarda.
const { name: pageTransition, setBack } = usePageTransition()
const scrollMemory = createScrollMemory({ router, pane: () => pane.value, onNavigated: setBack })
onUnmounted(() => scrollMemory.stop())
const navigating = useNavigationProgress(router)

const userOpen = ref(false)
const { preference: themePreference, cyclePreference } = useTheme()
// A theme picked in the ERP goes to the ERP account, so the person's other
// devices follow (see app/accountPreferences.js). Registered by this shell and
// not globally: the customer portal saves to ITS account, and a staff member
// signed into both in one browser must not have one overwrite the other.
let stopSavingTheme = null
const savePreferences = preferenceSaver(auth.savePreferences, isDemoInstance)
onMounted(() => { stopSavingTheme = onThemeChosen((theme) => savePreferences({ theme })) })
onUnmounted(() => stopSavingTheme?.())
const themeTitle = computed(() =>
  t('nav.themeNow', { name: t(`nav.themes.${themePreference.value}`) }))
// Injected by vite from platform/server's package.json — the same number the
// module compatibility check reads. No fallback: a footer that invents a
// version is worse than one that shows nothing.
const platformVersion = import.meta.env.VITE_PLATFORM_VERSION ?? ''

const groups = computed(() => visibleNavigation(auth.can))

// Decided once for the whole sidebar rather than asked of each row, so exactly
// one thing is ever marked as current: see `activeEntry` in app/navState.js for
// the two places where asking each row separately lit up two of them.
//
// Compared by identity and not by route, because the pair it exists to separate
// — the Producción row and its Taller page — share a route.
const active = computed(() => activeEntry(groups.value, route.path))
const isActive = (entry) => entry === active.value

// ── Folding the sections ─────────────────────────────────────────────────────
// Which sections and entries the person left open, remembered per browser. Only
// the ones they actually touched are stored, so changing a default later still
// reaches everybody who never expressed an opinion about it. The rules are in
// app/navState.js; what is here is the wiring to the route and to localStorage.

const folds = ref(readFolds(localStorage))

const isOpen = (group) => isGroupOpen(group, folds.value, isActive)
const isBranchOpen = (item) => branchIsOpen(item, folds.value, isActive)

// A fold closed over the page being read. Nothing forces a section open any
// more — closing the one you are standing in is the whole point of a fold — so
// the heading and the parent row take over carrying the mark that the open
// section would have shown on one of its rows.
const holdsCurrent = (group) => !isOpen(group) && holdsActive(group, isActive)
const branchHoldsCurrent = (item) => !isBranchOpen(item) && anyChildActive(item, isActive)

function remember(key, open) {
  folds.value = toggleFold(folds.value, key, open)
  writeFolds(localStorage, folds.value)
}

const toggleGroup = (group) => remember(group.id, isOpen(group))
const toggleBranch = (item) => remember(branchKey(item), isBranchOpen(item))

// ── La banda de módulo ──────────────────────────────────────────────────────
//
// `buildGroups` decide QUÉ va junto y de qué color; aquí solo se dibuja.

/**
 * Cómo se llama un módulo para una persona.
 *
 * `modules.<nombre>` es el bloque que cada módulo ya rellena en sus cuatro
 * idiomas para la pantalla de roles, así que esto no añade ni un texto nuevo
 * que traducir. Si faltara, sale el nombre de la carpeta: peor que la
 * traducción y mucho mejor que un hueco.
 */
const moduleLabel = (name) => (te(`modules.${name}`) ? t(`modules.${name}`) : name)

/** Las clases que marcan una fila como parte de una banda, y dónde empieza y acaba. */
const bandClass = (item) => (item.band ? {
  'sidebar__banded': true,
  'sidebar__banded--first': item.band.first,
  'sidebar__banded--last': item.band.last,
} : null)

/**
 * El color, como variable CSS y no como clase.
 *
 * Ocho clases `--colour-1..8` serían ocho reglas que dicen lo mismo, y el día
 * que la paleta crezca hay que tocar la hoja de estilos además de la regla. Con
 * una variable, el número que decide `navigationRules` entra tal cual.
 */
const bandStyle = (band) => (band ? { '--band': `var(--nav-band-${band.colour})` } : null)

/** '/quotes/catalog' → 'quotes-catalog', for the aria-controls id. */
const slug = (path) => String(path).replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '')

// ── Collapsing the sidebar itself ────────────────────────────────────────────
//
// Below the drawer breakpoint the sidebar is already either absent or covering
// the screen, so a rail there would be a third state nobody asked for: the
// preference is remembered, and simply not applied until the window is wide
// enough for the sidebar to be taking room from the page in the first place.

const wideQuery = window.matchMedia('(min-width: 901px)')
const wide = ref(wideQuery.matches)
const railPreference = ref(readRail(localStorage))

const railed = computed(() => railPreference.value && wide.value)
const rail = computed(() => railItems(groups.value))

function toggleRail() {
  railPreference.value = !railPreference.value
  writeRail(localStorage, railPreference.value)
  closeFlyout()
}

// ── The flyout of a railed entry ─────────────────────────────────────────────
//
// One panel doing two jobs: the name of an entry that lost its label to the
// rail, and the way into the pages of an entry that has children. The same
// panel because it is the same gesture — you point at an icon and the sidebar
// tells you what is behind it.

const flyout = ref(null)
const flyoutEl = ref(null)
let closeTimer

/**
 * Opens against the row that was pointed at, and is nudged up when what it
 * holds would run off the bottom of the window — a module with six screens
 * near the end of a full rail is exactly where that happens.
 */
async function openFlyout(event, item) {
  clearTimeout(closeTimer)
  const { top } = event.currentTarget.getBoundingClientRect()
  flyout.value = { item, top }

  await nextTick()
  const height = flyoutEl.value?.offsetHeight ?? 0
  const overflow = top + height + 8 - window.innerHeight
  if (overflow > 0) flyout.value = { item, top: Math.max(8, top - overflow) }
}

function closeFlyout() {
  clearTimeout(closeTimer)
  flyout.value = null
}

// A grace period, so crossing the seam between the rail and the panel does not
// close the thing you are reaching for.
function scheduleClose() {
  clearTimeout(closeTimer)
  closeTimer = setTimeout(() => { flyout.value = null }, 140)
}

const holdFlyout = () => clearTimeout(closeTimer)

// Nothing that hovers survives the page changing underneath it.
watch(() => route.path, () => {
  closeFlyout()
  navOpen.value = false
})
watch(railed, (on) => { if (!on) closeFlyout() })

async function signOut() {
  userOpen.value = false
  await auth.logout()
  router.push('/login')
}

// Ctrl+B for the navigation, beside the Ctrl+K of the search box and the Ctrl+J
// of the assistant. On a narrow window there is no rail to collapse to, so the
// same key opens and closes the drawer instead.
function onKeydown(event) {
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'b') {
    event.preventDefault()
    if (wide.value) toggleRail()
    else navOpen.value = !navOpen.value
    return
  }

  if (event.key === 'Escape') {
    closeFlyout()
    navOpen.value = false
    userOpen.value = false
  }
}

const closeUserMenu = () => { userOpen.value = false }
const onWidthChange = (event) => { wide.value = event.matches }

onMounted(() => {
  document.addEventListener('click', closeUserMenu)
  document.addEventListener('keydown', onKeydown)
  wideQuery.addEventListener('change', onWidthChange)
})
onUnmounted(() => {
  document.removeEventListener('click', closeUserMenu)
  document.removeEventListener('keydown', onKeydown)
  wideQuery.removeEventListener('change', onWidthChange)
  clearTimeout(closeTimer)
})
</script>

<style scoped>
/*
  An app shell, not a long document: the window itself never scrolls, and the
  page content scrolls inside its own pane. That is what keeps the sidebar and
  the top bar where the user left them — previously the whole grid grew with
  the content, so scrolling a long settings page carried the navigation off the
  top of the screen with it.
*/
.shell {
  display: grid;
  grid-template-columns: var(--sidebar-width) 1fr;
  height: 100vh;
  height: 100dvh;
  /* Lo que queda por encima del teclado (ui-kit utils/viewport.js): con él
     fuera, el shell encoge y la caja en la que se escribe sigue a la vista. */
  height: var(--app-height, 100dvh);
  /* El transform que lo sigue cuando iOS desplaza lo que se ve va abajo, en el
     bloque sin scope, y SOLO con el teclado fuera: ver allí por qué. */
  overflow: hidden;
  transition: grid-template-columns var(--base);
}
.shell--rail { grid-template-columns: var(--sidebar-rail) 1fr; }

/* ── Sidebar ─────────────────────────────────────────────────────────── */
.sidebar {
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: var(--sidebar-bg);
  color: var(--sidebar-text);
  border-right: 1px solid var(--sidebar-border);
  /* The distance from an entry to the left edge of the panel, in one place:
     the active bar is drawn out there, and has to keep hugging the edge when
     the rail changes the padding under it. */
  --nav-pad: var(--space-3);
  /* The line the pages of an entry hang from. */
  --thread: var(--sidebar-thread);
}

/* A ring in the brand colour is invisible against navy. Inside the sidebar the
   focus ring is white, and drawn just inside the entry so it cannot be read as
   belonging to the one above. */
.sidebar :focus-visible {
  outline: 2px solid var(--sidebar-focus);
  outline-offset: -2px;
}

.sidebar__brand {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  /* Exactly as tall as the top bar beside it, so the two bottom borders read
     as one line across the whole window. */
  height: var(--topbar-height);
  flex-shrink: 0;
  padding: 0 var(--space-5);
  border-bottom: 1px solid var(--sidebar-line);
}
.sidebar--rail .sidebar__brand { padding: 0; justify-content: center; }

.sidebar__name { color: var(--sidebar-strong); font-weight: 600; font-size: var(--text-sm); }

/* The nav is the part that scrolls when a client installs enough modules to
   overflow: the brand and the version footer stay put. */
.sidebar__nav {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: var(--space-4) var(--nav-pad);
  /* A default scrollbar is a pale slab cut into a navy panel. Thin, translucent
     and out of the way until there is something worth scrolling. */
  scrollbar-width: thin;
  scrollbar-color: var(--sidebar-scroll) transparent;
}
.sidebar__nav::-webkit-scrollbar { width: 10px; }
.sidebar__nav::-webkit-scrollbar-track { background: transparent; }
.sidebar__nav::-webkit-scrollbar-thumb {
  background: var(--sidebar-scroll);
  border: 3px solid transparent;
  background-clip: content-box;
  border-radius: var(--radius-full);
}
.sidebar__nav:hover::-webkit-scrollbar-thumb {
  background: var(--sidebar-scroll-hover);
  background-clip: content-box;
}

/* A few pixels between entries so the hover and active backgrounds never touch
   each other: flush boxes read as one block that lights up in the wrong place. */
.sidebar__group {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
/* El aire de arriba es lo único que distingue el rango de un rótulo, y aquí hay
   dos rangos: la cabecera de una SECCIÓN («Plataforma») y el rótulo de un
   MÓDULO dentro de ella («Personal», sobre sus dos entradas). Estaban con 12 px
   y 11 px, que a ojo es el mismo hueco: dos jerarquías dibujadas igual son una
   sola, y el lector deja de saber si «Personal» abre una sección o etiqueta las
   dos líneas de debajo.
   Ahora el doble para la sección. Y los dos rótulos, pegados a lo suyo: un
   título pertenece a lo que va DEBAJO, así que debajo no lleva más que los 3 px
   que separan cualquier par de entradas. */
.sidebar__group + .sidebar__group { margin-top: var(--space-5); }

.sidebar__group-title {
  padding: var(--space-1) var(--space-3);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--sidebar-mute);
}

.sidebar__group-toggle {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  width: 100%;
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  text-align: left;
  font-family: inherit;
  transition: background var(--fast), color var(--fast);
}
.sidebar__group-toggle:hover { background: var(--sidebar-hover); color: var(--sidebar-strong); }

/*
  A section closed over the page being read. The fold now wins over the route —
  you can close the section you are standing in — so this is what is left of the
  highlight the fold took away: the heading a shade brighter, the count of what
  is behind it in the accent, and the same edge bar the active row draws, shorter
  because a heading is not a row you can go to.
*/
.sidebar__group-toggle--current { color: var(--sidebar-text); }
.sidebar__group-toggle--current .sidebar__count {
  background: var(--sidebar-accent);
  color: var(--color-text-on-primary);
  font-weight: 700;
}

.sidebar__chevron { flex-shrink: 0; transition: transform var(--base); }
.sidebar__chevron--closed { transform: rotate(-90deg); }

.sidebar__count {
  flex-shrink: 0;
  min-width: 17px;
  padding: 0 5px;
  border-radius: var(--radius-full);
  background: var(--sidebar-chip);
  font-size: 9px;
  line-height: 15px;
  text-align: center;
  letter-spacing: 0;
}

/*
  Folding without measuring anything in JavaScript: a grid row animated from
  0fr to 1fr collapses to exactly the height of its content and back.
*/
.sidebar__items {
  display: grid;
  grid-template-rows: 1fr;
  transition: grid-template-rows var(--base);
}
.sidebar__items--closed { grid-template-rows: 0fr; }

.sidebar__items-inner {
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.sidebar__link {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-sm);
  color: var(--sidebar-text);
  font-size: var(--text-sm);
  text-decoration: none;
  transition: background var(--fast), color var(--fast);
}
.sidebar__link:hover { background: var(--sidebar-hover); color: var(--sidebar-strong); text-decoration: none; }
.sidebar__badge {
  margin-left: auto;
  min-width: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: var(--color-primary);
  color: var(--color-text-on-primary);
  font-size: var(--text-xs);
  font-weight: 600;
  line-height: 18px;
  text-align: center;
}
.sidebar__link--active { background: var(--sidebar-active); color: var(--sidebar-strong); font-weight: 600; }

/*
  Where you are, marked against the edge of the panel rather than inside the
  row. A background one shade lighter than its neighbours is easy to lose in a
  list of twenty; a bar at the edge is not, and it is the one thing in the
  sidebar that carries the client's colour.
*/
.sidebar__link--active:not(.sidebar__link--child)::before,
.sidebar__row.sidebar__link--active::before,
.sidebar__row--current::before,
.sidebar__group-toggle--current::before {
  content: '';
  position: absolute;
  left: calc(-1 * var(--nav-pad));
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 18px;
  border-radius: 0 var(--radius-full) var(--radius-full) 0;
  background: var(--sidebar-accent);
}
/* A heading is shorter than a row, and the bar over it should not claim more
   height than the thing it belongs to. */
.sidebar__group-toggle--current::before { height: 12px; }

/* ── De qué módulo es cada entrada ────────────────────────────────────────── */
/*
  Un menú con once módulos es una lista de diecisiete nombres, y de esos
  diecisiete hay pares que son la misma cosa comprada: «Mi jornada» y
  «Fichajes», «Mi vehículo» y «Flota», «Mi día» y «Órdenes de trabajo». Sin
  nada que los una, un visitante cuenta diecisiete productos.

  Se marcan con dos cosas, y hacen falta las dos:

    el NOMBRE del módulo encima, que es lo que dice cuál es. Va primero porque
    es lo único que sigue funcionando si el color no se ve — pantalla mala, luz
    de una sala de reuniones, o alguien que no distingue el verde del rojo;

    un FILETE de color a la izquierda, que es lo que dice dónde empieza y dónde
    acaba sin tener que leer nada.

  El color no identifica al módulo, solo empareja sus filas: por eso ocho tonos
  bastan para veinte módulos, y por eso se reparten por orden de aparición en el
  menú (ver navigationRules.js).

  Los ocho tonos van en HSL con una misma saturación y una misma luminosidad, no
  elegidos a ojo: así ninguno pesa más que otro, que es lo que deja de pasar en
  cuanto se mezclan colores de marca.

  Y van DOS VECES, uno por tema. Es lo único que cambia entre ellos: el mismo
  tono, con la luminosidad al otro lado. Los de aquí son para el menú claro —un
  amarillo al 60% sobre blanco es un punto que no se ve— y los de abajo, los de
  siempre, para el oscuro. Antes había un solo juego porque el menú era oscuro
  en los dos temas, y esa premisa dejó de ser cierta el día que el tema claro
  pasó a tener el menú blanco.
*/
.sidebar {
  --nav-band-1: hsl(210 70% 45%);
  --nav-band-2: hsl(150 55% 35%);
  --nav-band-3: hsl(35 85% 40%);
  --nav-band-4: hsl(280 55% 52%);
  --nav-band-5: hsl(190 65% 36%);
  --nav-band-6: hsl(340 65% 48%);
  --nav-band-7: hsl(95 45% 34%);
  --nav-band-8: hsl(15 75% 45%);
}

:root[data-theme='dark'] .sidebar {
  --nav-band-1: hsl(210 70% 62%);
  --nav-band-2: hsl(150 55% 52%);
  --nav-band-3: hsl(35 85% 60%);
  --nav-band-4: hsl(280 55% 68%);
  --nav-band-5: hsl(190 65% 55%);
  --nav-band-6: hsl(340 65% 65%);
  --nav-band-7: hsl(95 45% 55%);
  --nav-band-8: hsl(15 75% 63%);
}

/* El nombre del módulo. Más callado que el título de sección —eso es una parte
   del menú, esto es una etiqueta dentro de ella— y con el color solo en el
   punto, para que la lista siga leyéndose en gris. */
.sidebar__band-title {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin: var(--space-2) 0 0;
  padding-left: calc(var(--space-3) + 9px);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--sidebar-soft);
}
.sidebar__band-title::before {
  content: '';
  width: 5px;
  height: 5px;
  border-radius: var(--radius-full);
  background: var(--band);
}
/* La primera banda de la sección no necesita separarse de nada. */
.sidebar__items-inner > .sidebar__band-title:first-child { margin-top: 0; }

/* El filete. Va DENTRO del panel y no en su borde, porque el borde ya significa
   otra cosa —«estás aquí»— y dos marcas en la misma columna se leen como una
   sola mal hecha. */
.sidebar__banded { position: relative; }
.sidebar__banded > .sidebar__link,
.sidebar__banded.sidebar__link { padding-left: calc(var(--space-3) + 9px); }

.sidebar__banded::after {
  content: '';
  position: absolute;
  left: var(--space-3);
  /* Se come los 3px de hueco entre filas: un filete cortado cada tres píxeles
     es una línea de puntos, no una banda. */
  top: -3px;
  bottom: -3px;
  width: 2px;
  background: var(--band);
  opacity: 0.65;
  pointer-events: none;
}
.sidebar__banded--first::after {
  top: 0;
  border-radius: var(--radius-full) var(--radius-full) 0 0;
}
.sidebar__banded--last::after {
  bottom: 0;
  border-radius: 0 0 var(--radius-full) var(--radius-full);
}
/* En la fila que se está leyendo el filete sube a opacidad plena: la banda
   acompaña, no compite, salvo donde está el lector. */
.sidebar__banded.sidebar__link--active::after,
.sidebar__banded:has(.sidebar__link--active)::after { opacity: 1; }

/* ── An entry with pages under it ─────────────────────────────────────────── */
/* No gap: the thread runs from under the parent's icon into the first of its
   pages, and three pixels of nothing in the middle of a line is a broken line
   rather than breathing room. */
.sidebar__branch { display: flex; flex-direction: column; }

/* The link and the chevron share one row and one hover: two separate targets
   that light up independently read as two entries, which is the opposite of
   what the fold is for. */
.sidebar__row {
  position: relative;
  display: flex;
  align-items: center;
  border-radius: var(--radius-sm);
  transition: background var(--fast);
}
.sidebar__row:hover { background: var(--sidebar-hover); }
.sidebar__row:hover .sidebar__link--parent { color: var(--sidebar-strong); }

.sidebar__link--parent { flex: 1; min-width: 0; background: none; }
.sidebar__link--parent:hover { background: none; }
/* The bar belongs to the row, which is the box that spans the whole width. */
.sidebar__link--parent.sidebar__link--active::before { content: none; }

.sidebar__disclose {
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  margin-right: 4px;
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--sidebar-soft);
  transition: background var(--fast), color var(--fast);
}
.sidebar__disclose:hover { background: var(--sidebar-chip); color: var(--sidebar-strong); }

/*
  ── The thread ──────────────────────────────────────────────────────────────

  The pages of an entry hang off a line that drops from the centre of their
  parent's icon and curves into each one, the way a file tree does. It was a
  straight rule down the left with the pages beside it, which grouped them but
  did not attach them to anything: four small labels next to a line read as a
  second list, not as what is inside the row above.

  The thread sits 21px from the left of the group, which is not a number chosen
  to look right: it is where the centre of the parent's icon is (12px of
  padding, half of an 18px glyph). The line and the icon it falls from are the
  same vertical.
*/
.sidebar__subitems { margin-left: calc(var(--space-3) + 8px); }
.sidebar__subitems > .sidebar__items-inner {
  /* One pixel wider than the offset the thread is drawn at below, so the line
     lands just inside this box rather than exactly on the edge that
     `overflow: hidden` — the fold needs it — clips against. On the edge, a
     browser rounding the wrong way at some zoom levels erases the thread. */
  padding-left: calc(var(--space-3) + 1px);
  /* The line is drawn per page below, in the two pieces that meet at the
     middle of each row. Drawn once on this box instead, it would run past the
     last page to the bottom of the list and end pointing at nothing. */
  gap: 0;
}

/*
  A fixed height rather than padding, so the two halves of the thread meet at
  `50%` of a row whose height nothing else can move. The labels never wrap —
  they truncate — so there is no case where this clips anything.
*/
.sidebar__link--child {
  height: 28px;
  padding: 0 var(--space-2);
  font-size: var(--text-xs);
  color: var(--sidebar-text);
}
.sidebar__link--child.sidebar__link--active { color: var(--sidebar-strong); }

/* Down from the row above, then the curve into this one. */
.sidebar__link--child::before {
  content: '';
  position: absolute;
  left: calc(-1 * var(--space-3));
  top: 0;
  width: 10px;
  height: 50%;
  border-left: 1px solid var(--thread);
  border-bottom: 1px solid var(--thread);
  border-bottom-left-radius: 8px;
  pointer-events: none;
}

/* And on down to the next one. Not on the last, which is where the thread
   ends: a line that carries on past the final page suggests a page that is
   not there. */
.sidebar__link--child:not(:last-child)::after {
  content: '';
  position: absolute;
  left: calc(-1 * var(--space-3));
  top: 50%;
  bottom: 0;
  width: 1px;
  background: var(--thread);
  pointer-events: none;
}

/* Where you are, said by the thread itself. A child already sits inside a
   marked-out branch, so a second bar out at the panel edge would be marking
   the same thing twice from two different places. */
.sidebar__link--child.sidebar__link--active::before { border-color: var(--sidebar-accent); }

/* ── The collapsed sidebar ───────────────────────────────────────────────── */
.sidebar--rail .sidebar__nav { display: flex; flex-direction: column; gap: 3px; }

.sidebar__link--rail {
  justify-content: center;
  gap: 0;
  height: 40px;
  padding: 0;
  flex-shrink: 0;
}

/* All that is left of the section headings once there is no room to write
   them: the grouping survives, the words do not. */
.sidebar__divider {
  height: 0;
  margin: var(--space-2);
  border: none;
  border-top: 1px solid var(--sidebar-line);
}

.sidebar__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-5);
  border-top: 1px solid var(--sidebar-line);
  color: var(--sidebar-mute);
}
.sidebar--rail .sidebar__foot { justify-content: center; padding: var(--space-3) 0; }

.sidebar__collapse {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--sidebar-soft);
  transition: background var(--fast), color var(--fast);
}
.sidebar__collapse:hover { background: var(--sidebar-chip); color: var(--sidebar-strong); }

/* The chevron points the way the panel is about to move. */
.sidebar__collapse-icon { transform: rotate(90deg); transition: transform var(--base); }
.sidebar--rail .sidebar__collapse-icon { transform: rotate(-90deg); }

/* ── The flyout of a railed entry ────────────────────────────────────────── */
.flyout {
  position: fixed;
  left: var(--sidebar-rail);
  z-index: 90;
  min-width: 176px;
  max-width: 260px;
  padding: var(--space-2);
  background: var(--sidebar-bg);
  border: 1px solid var(--sidebar-line);
  border-radius: var(--radius);
  box-shadow: var(--shadow-lg);
  /* Pulled back over the panel edge so the pointer never crosses a gap on its
     way from the icon to what the icon opened. */
  margin-left: -1px;
}

.flyout__title {
  display: block;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-sm);
  color: var(--sidebar-strong);
  font-size: var(--text-sm);
  font-weight: 600;
  text-decoration: none;
}
.flyout__title--link:hover { background: var(--sidebar-hover); text-decoration: none; }

.flyout__link {
  display: block;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-sm);
  color: var(--sidebar-text);
  font-size: var(--text-xs);
  text-decoration: none;
}
.flyout__link:hover { background: var(--sidebar-hover); color: var(--sidebar-strong); text-decoration: none; }
.flyout__link--active { background: var(--sidebar-active); color: var(--sidebar-strong); font-weight: 600; }

/* ── Main column ─────────────────────────────────────────────────────── */
.shell__main {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

/* No longer sticky: it sits outside the scrolling pane, so it simply stays. */
.topbar {
  z-index: 50;
  display: flex;
  align-items: center;
  gap: var(--space-3);
  height: var(--topbar-height);
  flex-shrink: 0;
  padding: 0 var(--space-5);
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
}

.topbar__burger { display: none; border: none; background: none; color: var(--color-text-soft); }
/* El círculo de «tirar para recargar»: bajo la barra de arriba, centrado. */
.pull {
  position: fixed;
  top: calc(var(--viewport-top, 0px) + env(safe-area-inset-top, 0px) + 8px);
  left: 50%;
  z-index: 120;
  width: 40px; height: 40px;
  display: grid; place-items: center;
  border-radius: 50%;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  box-shadow: var(--shadow);
  color: var(--color-text-mute);
  pointer-events: none;
}
.pull--armed { color: var(--color-primary); }
.pull--busy svg { animation: pull-spin 0.8s linear infinite; }
@keyframes pull-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .pull--busy svg { animation: none; } }

/* La raya de «yendo a otra pantalla», pegada debajo de la barra de arriba.
   Alto cero en el flujo, para que aparecer y desaparecer no mueva el panel ni
   un píxel; la raya cuelga por encima del contenido. Indeterminada: no se
   sabe cuánto falta, y una barra que finge un porcentaje miente. */
.nav-progress {
  position: relative;
  height: 0;
  z-index: 5;
  pointer-events: none;
}
.nav-progress::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  height: 2px;
  width: 40%;
  border-radius: 0 2px 2px 0;
  background: var(--color-primary);
  animation: nav-progress 1.1s ease-in-out infinite;
}
@keyframes nav-progress {
  from { left: -40%; }
  to   { left: 100%; }
}
/* Sin movimiento: la raya entera y quieta, que sigue diciendo «yendo». */
@media (prefers-reduced-motion: reduce) {
  .nav-progress::before { width: 100%; animation: none; opacity: 0.6; }
}

/* El asistente en la barra solo existe en el teléfono (ver la regla de 640px). */
.topbar__phone { display: none; }

.topbar__icon {
  width: 36px; height: 36px;
  display: grid; place-items: center;
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  background: var(--color-surface);
  color: var(--color-text-soft);
}
.topbar__icon:hover { color: var(--color-text); }

/*
  The one scrolling pane in the application. Everything that wants to stick to
  the bottom of the viewport — the unsaved-changes bars — sticks inside this.

  `overscroll-behavior: contain` stops a scroll that reaches the end of this
  pane from chaining outwards to the document. Without it, scrolling past the
  bottom of a long page tugs at the window behind and reads as a second
  scrollbar even when there is only one.
*/
.shell__content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  /*
    And never sideways. `overflow-y: auto` alone turns the other axis to `auto`
    too, so one element a few pixels too wide made the WHOLE page slide left and
    right under the thumb — the pane wobbling is the single most un-app-like
    thing a phone can show. Wide content scrolls inside its own box (tables,
    the planning board); what does not fit is a bug to fix at its source, and
    the mobile sweep still measures `scrollWidth` through this.
  */
  overflow-x: hidden;
}

/* The shell's own chrome is furniture, not text: a long press on a menu entry
   or the top bar selected its label instead of doing nothing, like a button. */
.sidebar,
.topbar {
  -webkit-user-select: none;
  user-select: none;
}
.topbar :is(input, textarea) {
  -webkit-user-select: text;
  user-select: text;
}

/* ── User menu ───────────────────────────────────────────────────────── */
.usermenu { position: relative; }

.usermenu__trigger {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-1) var(--space-2);
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  background: var(--color-surface);
  max-width: 200px;
}


/* ── Con el dedo ────────────────────────────────────────────────────────────
   El menú de la cuenta medía 36x36 en un móvil: por ahí se sale del ERP, se
   cambia de idioma y se cambia de empresa.
   `pointer: coarse` y no un ancho de pantalla: lo que decide es con qué se
   toca — una tableta con teclado tiene pantalla de sobra y se sigue tocando
   con el dedo. */
@media (pointer: coarse) {
  .usermenu__trigger { min-height: 44px; }
  .usermenu__trigger { min-width: 44px; justify-content: center; }
}

.usermenu__avatar {
  width: 26px; height: 26px;
  display: grid; place-items: center;
  border-radius: var(--radius-full);
  background: var(--color-primary-soft);
  color: var(--color-primary);
  font-size: 10px;
  font-weight: 700;
}
.usermenu__name { font-size: var(--text-sm); }

.usermenu__panel {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 60;
  min-width: 220px;
  padding: var(--space-2);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-lg);
}

.usermenu__email {
  padding: var(--space-2) var(--space-3);
  font-size: var(--text-xs);
  color: var(--color-text-mute);
  border-bottom: 1px solid var(--color-border);
  margin-bottom: var(--space-1);
}

.usermenu__item {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  width: 100%;
  padding: var(--space-2) var(--space-3);
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  font-size: var(--text-sm);
  color: var(--color-text);
  text-align: left;
  text-decoration: none;
}
.usermenu__item:hover { background: var(--color-gray-100); text-decoration: none; }

.usermenu__section {
  padding: var(--space-2) var(--space-3);
  margin: var(--space-1) 0;
  border-top: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);
}
/* Theme and language sit one after the other: one line between them, not the
   bottom of the first against the top of the second. */
.usermenu__section + .usermenu__section {
  margin-top: calc(-1 * var(--space-1));
  border-top: none;
}
.usermenu__label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-text-mute);
  margin-bottom: var(--space-2);
}

/* ── Mobile ──────────────────────────────────────────────────────────── */
.shell__backdrop {
  position: fixed;
  inset: 0;
  z-index: 70;
  background: rgba(15, 23, 42, 0.4);
}

/* Only ever visible where the top bar has no room for its own copy. */
.usermenu__narrow { display: none; }
.usermenu__install { width: 100%; justify-content: center; margin-top: var(--space-1); }

@media (max-width: 900px) {
  /* The rail belongs to a window wide enough for the sidebar to be taking room
     away from the page. Here it is a drawer instead, and `railed` is false
     whatever the stored preference says — see the matchMedia in the script. */
  .shell,
  .shell--rail { grid-template-columns: 1fr; }

  .sidebar {
    position: fixed;
    inset: 0 auto 0 0;
    z-index: 80;
    width: var(--sidebar-width);
    max-width: 85vw;
    transform: translateX(-100%);
    transition: transform var(--base);
    /* The drawer covers the left edge of the screen, notch included. */
    padding-left: env(safe-area-inset-left);
  }
  .shell--nav-open .sidebar { transform: none; }

  .topbar__burger { display: grid; place-items: center; width: 40px; height: 40px; }

  /* ── El cajón se toca con un dedo ─────────────────────────────────────────

     Todo lo de aquí dentro estaba dimensionado para un ratón, que es lo que
     usa quien tiene el menú fijo a la izquierda. En un cajón no: el cajón SOLO
     existe en un móvil, y ahí lo mismo se toca con el pulgar.

     Medido a 390 px con el cajón abierto: los enlaces de una sección plegada
     tenían 19 px de alto, el título de sección 24 y la flecha que pliega 28.
     Nada de eso se falla una vez — se falla dos, y a la segunda la persona
     abre la sección que no quería y pierde el sitio.

     44 px es lo que recomiendan Apple y Google, y lo escriben personas sin
     guantes. Se sube el alto y no el relleno donde el hilo de la izquierda
     depende de la altura de la fila: se encuentra en el 50 % de la fila, y una
     fila más alta lo sigue cumpliendo. */
  .sidebar__group-toggle { min-height: 40px; }

  .sidebar__link--child {
    /* Alto y no relleno: el hilo que baja de la fila de arriba busca el 50 %
       de esta, y con relleno la altura la decidiría el texto. */
    height: 40px;
    font-size: var(--text-sm);
  }

  .sidebar__disclose { width: 40px; height: 40px; }

  /* Y este directamente no pinta nada aquí: contrae la barra a un raíl, y en
     un cajón no hay raíl que valga — `railed` es falso por debajo de 900 px
     diga lo que diga la preferencia guardada. Era un botón de 32 px que no
     hacía nada visible, justo encima del borde de la pantalla. */
  .sidebar__collapse { display: none; }
}

/* ── Phone ───────────────────────────────────────────────────────────────── */
@media (max-width: 640px) {
  .topbar {
    gap: var(--space-2);
    padding: 0 var(--space-3);
    padding-left: max(var(--space-3), env(safe-area-inset-left));
    padding-right: max(var(--space-3), env(safe-area-inset-right));
  }

  /* Moved into the user menu; see the panel above. */
  .topbar__wide { display: none; }
  .topbar__phone { display: grid; width: 40px; height: 40px; color: var(--color-primary); }

  .usermenu__narrow { display: block; }

  /*
    The avatar alone. The name was the widest thing in the bar and it says
    nothing the avatar does not — you know who you are signed in as.
  */
  .usermenu__name { display: none; }
  .usermenu__trigger {
    padding: 0;
    border-color: transparent;
    background: none;
  }
  .usermenu__avatar { width: 34px; height: 34px; font-size: var(--text-xs); }

  /* Anchored to the right edge of the window rather than to the avatar, so a
     220px panel cannot hang off the side of a 390px screen. */
  .usermenu__panel {
    position: fixed;
    top: calc(var(--topbar-height) + 4px);
    right: var(--space-3);
    left: var(--space-3);
    min-width: 0;
  }
}

/* ── El cajón, con el dedo ───────────────────────────────────────────────────

   En un móvil este cajón es la ÚNICA navegación que hay: no existe la columna
   de la izquierda, y la barra de abajo solo lleva las cuatro de campo. Las
   filas medían 36 px y las de segundo nivel 28, que es la mitad de lo que pide
   un dedo, y están una encima de otra — fallar el toque no es no llegar, es
   entrar en otra pantalla.

   `pointer: coarse` y no un ancho: lo que decide es con qué se toca, no cuánto
   mide la ventana; una tableta con teclado tiene pantalla de sobra y se sigue
   tocando con el dedo.

   El hijo mantiene ALTURA FIJA y no relleno, como manda el comentario del hilo
   de más arriba: la curva se dibuja al 50% de la fila, así que la fila puede
   crecer pero tiene que seguir midiendo algo exacto.

   El chevron va a 40 y no a 44 de ancho a propósito: cada píxel que se lleva
   se lo quita a la etiqueta, que ya se corta, y 40x44 es objetivo de sobra. */
@media (pointer: coarse) {
  .sidebar__link { min-height: 44px; }
  .sidebar__link--child { height: 44px; }
  .sidebar__group-toggle { min-height: 44px; }
  .sidebar__disclose { width: 40px; height: 44px; }
}
</style>

<style>
/*
  Con el teclado fuera, iOS además desplaza lo que se ve para enseñar la caja
  (--viewport-top): el armazón va con ello, o la cabecera se queda por encima
  del borde de la pantalla. El transform hace del armazón el contenedor de todo
  `position: fixed` que lleve dentro (el cajón, la barra de acciones anclada, el
  botón flotante del calendario), que así se apoya en las teclas sin tocar cada
  uno.

  Solo mientras se escribe, y no siempre: un transform también hace del
  armazón una capa propia, y todo lo suyo pasaba a pintarse por debajo de lo
  que vive en <body> con cualquier z-index — la pestaña del asistente (130)
  encima del árbol a pantalla completa y del panel lateral de una obra (180),
  que se ponen por encima de ella a propósito. Sin teclado lo que se ve es la
  ventana entera y el transform no aporta nada.

  Sin scope: `:global(html.x) .y` en un bloque scoped se compila a `html.x`.
*/
html.keyboard-open .shell { transform: translateY(var(--viewport-top, 0px)); }

/*
  La pantalla nueva entrando, con el dedo (app/pageTransition.js decide el
  nombre; con ratón o con «menos movimiento» es `fade`, de base.css).

  Sin scope: las clases caen en la raíz de la pantalla, que es de otro
  componente.

  Con `left` sobre `position: relative` y NO con `transform`, y es lo que
  importa: un `transform` en la raíz de la pantalla la convierte en el bloque
  contenedor de todo lo `position: fixed` que lleve dentro —la barra de
  acciones anclada de una ficha, el botón flotante del calendario, treinta y
  tantos más en los módulos— y durante los 200 ms del deslizamiento saldrían
  pintados en mitad de la página y saltarían a su sitio al acabar. Un
  desplazamiento relativo no crea ese contenedor.

  La salida es un fundido cortísimo: con `out-in` la nueva espera a que la
  vieja se vaya, y lo que se nota es la entrada. Los 24 px no llegan al borde:
  `.shell__content` tiene `overflow-x: hidden`, no sale barra lateral.
*/
.page-forward-enter-active,
.page-back-enter-active {
  position: relative;
  transition: opacity 180ms ease-out, left 200ms cubic-bezier(0.16, 1, 0.3, 1);
}
.page-forward-leave-active,
.page-back-leave-active { transition: opacity 70ms linear; }
.page-forward-enter-from { opacity: 0; left: 24px; }
.page-back-enter-from { opacity: 0; left: -24px; }
.page-forward-enter-to,
.page-back-enter-to { left: 0; }
.page-forward-leave-to,
.page-back-leave-to { opacity: 0; }
</style>
